/**
 * La banda del modo soporte · `BFF_BO.md` §8.1.
 *
 * «Mientras dura, toda la interfaz lleva una banda visible: Estás dentro de Dojang
 * Pascual en modo soporte · Salir». Arriba de todo, en todas las pantallas, y con un
 * color que no se confunde con el del club: quien la ve tiene que saber en cada
 * momento que **está en la cuenta de otro**.
 *
 * Dos estados y se distinguen a la primera:
 *
 * - **Solo lectura**, que es como nace. Se ve todo y no se cambia nada.
 * - **Escritura**, en ámbar y diciéndolo: cada cambio pedirá confirmación, y
 *   queda a tu nombre en el registro del club.
 *
 * Entrar en escritura se confirma aquí mismo, en la banda, y volver a solo lectura
 * no: quitarse permisos nunca tiene que costar un clic de más.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import {
  fijarEntradaEnSoporte,
  olvidarEntrada,
  type EntradaEnSoporte,
} from '@/shared/api/soporte';
import { fechaYHora } from '@/shared/ui/formato';

export function BandaDeSoporte({ entrada }: { entrada: EntradaEnSoporte }) {
  const cache = useQueryClient();
  const ir = useNavigate();
  const [pidiendo, setPidiendo] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['admin', 'soporte'] });
  };

  const escritura = useMutation({
    mutationFn: async (encender: boolean) => {
      const ruta = api.v1.admin['support-sessions']({ id: entrada.id }).write;
      const r = encender ? await ruta.post() : await ruta.delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return encender;
    },
    onSuccess: (encender) => {
      setPidiendo(false);
      setProblema(null);
      fijarEntradaEnSoporte({ ...entrada, escritura: encender });
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const salir = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin['support-sessions']({
        id: entrada.id,
      }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
    },
    onSuccess: () => {
      olvidarEntrada();
      fijarEntradaEnSoporte(null);
      /*
       * Lo del club, fuera de la caché: lo que se vio en soporte no tiene por qué
       * seguir en memoria al volver al back office.
       */
      cache.removeQueries({
        predicate: (q) => !(q.queryKey[0] === 'admin'),
      });
      refrescar();
      void ir({ to: '/admin/soporte' });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const trabajando = escritura.isPending || salir.isPending;

  return (
    <div
      className={`banda-soporte${entrada.escritura ? ' banda-soporte-escritura' : ''}`}
      role="status"
    >
      <p className="banda-soporte-texto">
        <strong>
          Estás dentro de {entrada.nombre} en modo soporte
          {entrada.escritura ? ', con escritura' : ', solo lectura'}.
        </strong>{' '}
        {entrada.escritura
          ? 'Cada cambio te pedirá confirmación y quedará a tu nombre en su registro de accesos.'
          : 'Ves lo mismo que el dueño del club y no puedes cambiar nada.'}{' '}
        <span className="banda-soporte-hora">
          Caduca el {fechaYHora(entrada.expira)}.
        </span>
      </p>

      {problema && (
        <p className="banda-soporte-error" role="alert">
          {problema.detail ?? problema.title}
        </p>
      )}

      {pidiendo ? (
        <div className="banda-soporte-acciones">
          <span>
            <strong>¿Entrar en modo escritura?</strong> Podrás cambiar datos de{' '}
            {entrada.nombre} de verdad. Te pediremos confirmación antes de cada
            cambio.
          </span>
          <button
            type="button"
            className="btn-peligro"
            disabled={trabajando}
            onClick={() => escritura.mutate(true)}
          >
            {escritura.isPending ? 'Entrando…' : 'Sí, entrar en escritura'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={trabajando}
            onClick={() => setPidiendo(false)}
          >
            No, seguir solo mirando
          </button>
        </div>
      ) : (
        <div className="banda-soporte-acciones">
          {entrada.escritura ? (
            <button
              type="button"
              className="btn-plano"
              disabled={trabajando}
              onClick={() => escritura.mutate(false)}
            >
              Volver a solo lectura
            </button>
          ) : (
            <button
              type="button"
              className="btn-plano"
              disabled={trabajando}
              onClick={() => {
                setProblema(null);
                setPidiendo(true);
              }}
            >
              Entrar en modo escritura
            </button>
          )}
          <button
            type="button"
            className="btn-plano"
            disabled={trabajando}
            onClick={() => salir.mutate()}
          >
            {salir.isPending ? 'Saliendo…' : 'Salir del modo soporte'}
          </button>
        </div>
      )}
    </div>
  );
}
